/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { PrimitiveValue, PropertyDescription, PropertyRecord, PropertyValueFormat } from "@itwin/appui-abstract";
import { PropertyEditorProps } from "@itwin/components-react";
import { Input } from "@itwin/itwinui-react";
import { WithConstraints } from "../../common/ContentBuilder.js";
import { PropertyEditorAttributes } from "../editors/Common.js";
import { applyNumericConstraints, getDecimalRoundingError, getMinMaxFromPropertyConstraints } from "./Utils.js";

/** @internal */
// eslint-disable-next-line @typescript-eslint/no-deprecated
export interface NumericPropertyInputProps extends PropertyEditorProps {
  propertyRecord: PropertyRecord;
}

/** @internal */
export const NumericPropertyInput = forwardRef<PropertyEditorAttributes, NumericPropertyInputProps>((props, ref) => {
  const { onCommit, propertyRecord, setFocus, onCancel } = props;
  const property: WithConstraints<PropertyDescription> = propertyRecord.property;

  const [inputValue, setInputValue] = useState<string>(() => getInputTargetFromPropertyRecord(propertyRecord) ?? "");
  const [isEditing, setEditing] = useState(false);
  const displayValue = useMemo(
    () => (!isEditing && propertyRecord.isMerged && inputValue === "" ? "--" : inputValue),
    [isEditing, propertyRecord.isMerged, inputValue],
  );

  const handleChange = (newVal: string) => {
    setInputValue(newVal);
  };

  const { min, max } = getMinMaxFromPropertyConstraints(property.constraints);
  const commitInput = () => {
    const formattedInputValue = applyConstraints({ inputAsString: inputValue, min, max });
    setInputValue(formattedInputValue);
    onCommit && onCommit({ propertyRecord, newValue: parsePrimitiveValue(formattedInputValue) });
  };

  return (
    <NumericInput
      onChange={handleChange}
      value={displayValue}
      onCancel={onCancel}
      onBlur={() => {
        commitInput();
        setEditing(false);
      }}
      onFocus={() => setEditing(true)}
      isDisabled={propertyRecord.isReadonly}
      setFocus={setFocus}
      ref={ref}
      min={min}
      max={max}
    />
  );
});
NumericPropertyInput.displayName = "NumericPropertyInput";

function parsePrimitiveValue(value: string): PrimitiveValue {
  const isValid = value && !isNaN(Number(value));
  return {
    valueFormat: PropertyValueFormat.Primitive,
    value: isValid ? Number(value) : undefined,
    displayValue: value,
    roundingError: isValid ? getDecimalRoundingError(value) : undefined,
  };
}

function getInputTargetFromPropertyRecord(propertyRecord: PropertyRecord) {
  const value = propertyRecord.value;
  /* v8 ignore next 3 -- @preserve */
  if (value.valueFormat !== PropertyValueFormat.Primitive) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return value.value?.toString();
}

/** @internal */
// eslint-disable-next-line @typescript-eslint/no-deprecated
export interface NumericInputProps extends PropertyEditorProps {
  onChange: (newValue: string) => void;
  onBlur?: React.FocusEventHandler;
  onFocus?: React.FocusEventHandler;
  value: string;
  isDisabled?: boolean;
  min?: number;
  max?: number;
}

/** @internal */
export const NumericInput = forwardRef<PropertyEditorAttributes, NumericInputProps>(
  ({ value, onChange, onBlur, onFocus, isDisabled, setFocus, min, max, onCancel }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => ({ getValue: () => parsePrimitiveValue(value), htmlElement: inputRef.current }), [
      value,
    ]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget.value;
      // Check if it is a correct number and it is not infinity.
      if (!isNaN(Number(val)) && isFinite(Number(val))) {
        onChange(val);
      }
      // Number{"+"), Number("-") and Number(".") returns NaN, but if input is only `.`, `-` or `+`, we should fire `onChange` function.
      else if (val.length === 1 && "+-.".includes(val)) {
        onChange(val);
      }
      // Number("+.") and Number("-.") returns NaN, but if input is only `+.` or `-.`, we want to fire `onChange` function.
      else if (val === "+." || val === "-.") {
        onChange(val);
      }
      // Let user write scientific numbers. Number("1e") returns NaN, but we want to fire `onChange` function when input before `e` is a correct number.
      else if (val.endsWith("e") && !isNaN(Number(val.slice(0, val.length - 1))) && val.length !== 1) {
        onChange(val);
      }
      // Let user write scientific numbers. Number("1e-") returns NaN, but we want to fire `onChange` function when input before `e-` is a correct number.
      // We don't need to check if string before `e-` is a valid number, because there is a check if string before `e` is a correct number.
      else if (val.endsWith("e-")) {
        onChange(val);
      }
    };

    useEffect(() => {
      if (setFocus) {
        inputRef.current && inputRef.current.focus();
      }
    }, [setFocus]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        onCancel?.();
      }
      if (e.key === "Enter") {
        inputRef.current?.blur();
        e.stopPropagation();
      }
    };

    return (
      <Input
        ref={inputRef}
        disabled={isDisabled}
        data-testid="numeric-input"
        size="small"
        value={value}
        onKeyDown={handleKeyDown}
        min={min}
        max={max}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={(e) => {
          onFocus?.(e);
          requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(0, 9999);
          });
        }}
      />
    );
  },
);
NumericInput.displayName = "NumericInput";

function applyConstraints({ inputAsString, min, max }: { inputAsString: string; min?: number; max?: number }): string {
  if (min === undefined && max === undefined) {
    return inputAsString;
  }
  const inputAsNumber = Number(inputAsString);
  if (!isFinite(inputAsNumber)) {
    return inputAsString;
  }
  return applyNumericConstraints({ value: inputAsNumber, min, max }).toString();
}
